import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { t } from '../i18n';

/**
 * The console's ONE dialog (UX review 30 Sep, P1-4). Every modal — the rule editor, the
 * version line's settings / release / history, every create / edit / confirm dialog — is
 * this component on a native `<dialog>` opened with `showModal()`, which gives:
 *   • the rest of the page inert (no clicking or tabbing to what is behind the scrim);
 *   • Escape → `cancel`, handled here: it closes, unless a write is in flight (`busy`);
 *   • focus moved in on open (the first form field of the body, else the ✕) and back to
 *     whatever opened it on close;
 *   • Tab and Shift+Tab kept inside — wrapped here explicitly, so it holds in every browser
 *     and in jsdom alike;
 *   • the purple head's `<h2>` as the accessible name (`aria-labelledby`), unless a
 *     separate `a11yLabel` is given.
 *
 * A mouse press on the scrim still closes it, as every frame did before (same `busy` rule).
 *
 * Children that own the write (the release panel, version history) raise `busy` through
 * `useDialogBusy`, so the frame knows without the screen plumbing the flag through.
 */

/** A child in the body reports a write in flight (`useDialogBusy`). */
const BusyContext = createContext<((busy: boolean) => void) | null>(null);

/** Mark the enclosing `Dialog` busy while `busy` is true: Escape and the scrim do not close it. */
export function useDialogBusy(busy: boolean): void {
  const report = useContext(BusyContext);
  useEffect(() => {
    if (!report) return;
    report(busy);
    return () => report(false);
  }, [report, busy]);
}

const FIELD_SELECTOR = 'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])';
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([type="hidden"]):not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * The open modals, most recent last. A modal sits in the browser's top layer, above every
 * z-index, and makes the rest of the page inert — so anything that must show while one is
 * open (the shell's toast) is rendered INTO the top one (`useTopDialog`).
 */
const openDialogs: HTMLDialogElement[] = [];
const dialogListeners = new Set<() => void>();
const notifyDialogs = () => dialogListeners.forEach((fn) => fn());

function subscribeDialogs(fn: () => void): () => void {
  dialogListeners.add(fn);
  return () => dialogListeners.delete(fn);
}

/** The top-most open dialog, or null — re-renders the caller when it changes. */
export function useTopDialog(): HTMLDialogElement | null {
  return useSyncExternalStore(
    subscribeDialogs,
    () => openDialogs[openDialogs.length - 1] ?? null,
    () => null,
  );
}

/** Open modals, innermost last — for the page-scroll lock, which the first one takes and the last one gives back. */
let openCount = 0;
let savedOverflow = '';

function lockPageScroll(): void {
  if (openCount++ === 0) {
    savedOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
  }
}

function unlockPageScroll(): void {
  if (--openCount === 0) document.documentElement.style.overflow = savedOverflow;
}

/** Focusable elements of THIS dialog — not of a dialog nested inside it, and not hidden. */
function focusables(dialog: HTMLDialogElement, selector = FOCUSABLE_SELECTOR): HTMLElement[] {
  return [...dialog.querySelectorAll<HTMLElement>(selector)].filter(
    (el) => el.closest('dialog') === dialog && !el.closest('[hidden]') && !el.closest('.dlg-layer'),
  );
}

export interface DialogProps {
  /** The head's title — also the dialog's accessible name. */
  title: ReactNode;
  /** Head content after the title (an id, a chip), before the ✕. */
  head?: ReactNode;
  /** An accessible name other than the title (the rule editor names the rule). */
  a11yLabel?: string;
  onClose: () => void;
  children: ReactNode;
  /** The footer row: actions. Absent → no footer. */
  foot?: ReactNode;
  /** 940px instead of 560px. */
  wide?: boolean;
  /** Body children as they are, not as a 10px-gap stack (panels that bring their own sections). */
  plainBody?: boolean;
  testid?: string;
  /** A write is in flight: Escape, the scrim and ✕ do not close. */
  busy?: boolean;
  /** The ✕ in the head. Off only for a confirmation whose footer is the way out. */
  closeButton?: boolean;
  /** The ✕'s accessible name — default "Close". */
  closeLabel?: string;
  /** What gets focus on open; default: the body's first form field, else the ✕. */
  initialFocus?: RefObject<HTMLElement | null>;
}

export function Dialog({
  title,
  head,
  a11yLabel,
  onClose,
  children,
  foot,
  wide = false,
  plainBody = false,
  testid,
  busy = false,
  closeButton = true,
  closeLabel,
  initialFocus,
}: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const layerRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const [childBusy, setChildBusy] = useState(false);
  const isBusy = busy || childBusy;

  // Read by the native listeners, which are attached once.
  const onCloseRef = useRef(onClose);
  const busyRef = useRef(isBusy);
  onCloseRef.current = onClose;
  busyRef.current = isBusy;

  const requestClose = useCallback(() => {
    if (!busyRef.current) onCloseRef.current();
  }, []);

  useLayoutEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    // Blockly's pop-ups (a field's text box, a dropdown) are positioned in PAGE coordinates;
    // the overlay layer sits at the document's origin so they land where Blockly means.
    if (layerRef.current) {
      layerRef.current.style.left = `${-window.scrollX}px`;
      layerRef.current.style.top = `${-window.scrollY}px`;
    }
    lockPageScroll();
    if (!dialog.open) dialog.showModal();
    openDialogs.push(dialog);
    notifyDialogs();

    const target = initialFocus?.current ?? focusables(dialog, FIELD_SELECTOR).find((el) => el.closest('.dlg-body')) ?? closeRef.current ?? dialog;
    target.focus();

    // Native listeners: `cancel` must not reach a dialog around this one (React would bubble it up the tree).
    const onCancel = (e: Event) => {
      if (e.target !== dialog) return;
      e.preventDefault();
      requestClose();
    };
    // A browser may close a modal on a second Escape even when `cancel` was prevented.
    // Keep React's state the truth: reopen while busy, otherwise let the screen close it.
    // `close` is queued, not synchronous: one that arrives while the dialog is open again
    // (an effect re-run — React StrictMode mounts twice in development) is stale; ignore it.
    let closing = false;
    const onNativeClose = () => {
      if (closing || dialog.open) return;
      if (busyRef.current) dialog.showModal();
      else onCloseRef.current();
    };
    // Escape in one of Blockly's pop-ups (a field's text box, a dropdown — in `.dlg-layer`)
    // closes that pop-up, not the editor under it: a cancelled keydown makes no close request.
    // Capture phase, so it holds even if Blockly stops the key from bubbling.
    const onKeyCapture = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape' && e.target instanceof Element && e.target.closest('.dlg-layer') && e.target.closest('dialog') === dialog) e.preventDefault();
    };
    dialog.addEventListener('cancel', onCancel);
    dialog.addEventListener('close', onNativeClose);
    dialog.addEventListener('keydown', onKeyCapture, true);

    return () => {
      dialog.removeEventListener('cancel', onCancel);
      dialog.removeEventListener('close', onNativeClose);
      dialog.removeEventListener('keydown', onKeyCapture, true);
      closing = true;
      if (dialog.open) dialog.close();
      unlockPageScroll();
      const at = openDialogs.lastIndexOf(dialog);
      if (at >= 0) openDialogs.splice(at, 1);
      notifyDialogs();
      // Back to the opener — unless focus already moved somewhere real (not into the closing dialog).
      const active = document.activeElement;
      const lost = !active || active === document.body || dialog.contains(active);
      if (lost && opener && opener.isConnected) opener.focus();
    };
    // Opens once per mount; the latest onClose / busy are read through refs.
  }, []);

  const onKeyDown = (e: KeyboardEvent<HTMLDialogElement>) => {
    const dialog = ref.current;
    if (e.key !== 'Tab' || !dialog) return;
    // A dialog nested in this one handles its own keys.
    if ((e.target as Element).closest('dialog') !== dialog) return;
    const list = focusables(dialog);
    if (list.length === 0) {
      e.preventDefault();
      return;
    }
    const first = list[0]!;
    const last = list[list.length - 1]!;
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === dialog)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const onMouseDown = (e: MouseEvent<HTMLDialogElement>) => {
    // Only a press on this dialog's own scrim — not inside the panel, not on a nested dialog.
    if (e.target === ref.current || e.target === scrollRef.current) requestClose();
  };

  return (
    <dialog
      ref={ref}
      className="dlg-modal"
      aria-labelledby={a11yLabel ? undefined : titleId}
      aria-label={a11yLabel}
      data-testid={testid}
      onKeyDown={onKeyDown}
      onMouseDown={onMouseDown}
    >
      <div className="dlg-scroll" ref={scrollRef}>
        <div className={wide ? 'dlg' : 'dlg narrow'}>
          <div className="dlg-head">
            <h2 id={titleId}>{title}</h2>
            {head}
            {closeButton && (
              <button
                ref={closeRef}
                type="button"
                className="btn small dlg-close"
                onClick={requestClose}
                disabled={isBusy}
                aria-label={closeLabel ?? t('btn.close.label')}
              >
                ✕
              </button>
            )}
          </div>
          <BusyContext.Provider value={setChildBusy}>
            <div className={plainBody ? 'dlg-body' : 'dlg-body stack'}>{children}</div>
            {foot !== undefined && <div className="dlg-foot">{foot}</div>}
          </BusyContext.Provider>
        </div>
      </div>
      {/* Where Blockly's pop-ups live while this dialog is open (blocks/BlocksWorkspace.tsx). */}
      <div className="dlg-layer" ref={layerRef} />
    </dialog>
  );
}

/**
 * A yes/no question on top of whatever is open (QA 30 Sep: unsaved edits, a delete, discarding
 * the whole draft, signing out with one). The shared frame, so Escape, the scrim and focus work
 * as everywhere: Escape or "keep" (`onCancel`) closes only this question and leaves the screen
 * under it as it was; the action button (`onConfirm`) goes ahead. Focus starts on "keep" — the
 * answer that loses nothing.
 */
export function ConfirmDialog({
  title,
  children,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  danger = true,
  testid,
}: {
  title: ReactNode;
  children?: ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  /** The action throws something away (red); false for a neutral one. */
  danger?: boolean;
  testid?: string;
}) {
  const keep = useRef<HTMLButtonElement>(null);
  return (
    <Dialog
      title={title}
      testid={testid}
      onClose={onCancel}
      closeButton={false}
      initialFocus={keep}
      foot={
        <>
          <span className="spacer" />
          <button ref={keep} type="button" className="btn" data-testid={testid ? `${testid}-cancel` : undefined} onClick={onCancel}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className={danger ? 'btn danger' : 'btn primary'}
            data-testid={testid ? `${testid}-confirm` : undefined}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </>
      }
    >
      {children}
    </Dialog>
  );
}
